import React from 'react';
import { styled } from '@compiled/react';

import { BannerSpacer } from '@confluence/banners/entry-points/BannerSpacer.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { SuperAdminBanner } from '@confluence/super-admin/entry-points/SuperAdminBanner.ts';
import { PageWasInactiveBanner } from '@confluence/browser-status-indicator/entry-points/PageWasInactiveBanner.ts';
import { AdminAnnouncementBanner } from '@confluence/admin-announcement-banner/entry-points/AdminAnnouncementBanner.ts';
import { GracePeriodBanner } from '@confluence/trial-components/entry-points/GracePeriodBanner.tsx';
import { StorageEnforcementBanner } from '@confluence/storage-enforcement/entry-points/StorageEnforcementBanner.tsx';
import { CookiesConsentBanner } from '@confluence/cookies-consent-banner/entry-points/CookiesConsentBanner.tsx';
import { UnsupportedBrowserBanner } from '@confluence/unsupported-browser-banner';
import { DevToolsBanner } from '@confluence/dev-tools';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const BannerPositioner = styled.div({
	position: 'fixed',
	width: '100%',
	top: 0,
	left: 0,
	/* It has to be under all the drawers:
    - recent drawer
    - quick search
    - notification
    - app switcher
    - help menu
    But also ABOVE the page tree navigation.
    201 is an oddly specific number but the only
    one that covers all situations.
  */
	zIndex: 201,
});

type BannerContainerProps = {
	usePortalSpacer?: boolean;
};

const BannerContainerComponent = (props: BannerContainerProps) => {
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	return (
		<ErrorBoundary attribution={Attribution.ADMIN_EXPERIENCE}>
			<BannerPositioner data-vc="banner-container-component" {...ssrPlaceholderIdProp}>
				{/*---- Add your top-of-page banners here, in the order that they are to be shown (top to bottom) ---*/}
				<GracePeriodBanner />
				<SuperAdminBanner />
				<PageWasInactiveBanner />
				<StorageEnforcementBanner />
				<AdminAnnouncementBanner />
				<CookiesConsentBanner />
				<UnsupportedBrowserBanner />
				<DevToolsBanner />
				{/*---- End of the banner component list ---*/}
			</BannerPositioner>
			<BannerSpacer {...props} />
		</ErrorBoundary>
	);
};

export const BannerContainer = React.memo(BannerContainerComponent);
